import { AppRole, type OrganizationRole, OrganizationType } from '@craft/database';
import { type ExtractValues } from '@craft/lib';

import { isAppRole, isOrgType } from 'connect-graphql/auth/roles';

export const ANY_ORGANIZATION_TYPE = '__ANY_ORGANIZATION_TYPE__';
export type AnyOrganizationType = typeof ANY_ORGANIZATION_TYPE;

export enum AccessTypeId {
  Admin = 'Admin',
  EducationProviderAdmin = 'EducationProviderAdmin',
  EmployerAdmin = 'EmployerAdmin',
  Evaluator = 'Evaluator',
  Facilitator = 'Facilitator',
  Instructor = 'Instructor',
  Learner = 'Learner',
  Observer = 'Observer',
  SponsorAdmin = 'SponsorAdmin',
  SystemAdmin = 'SystemAdmin',
}

/**
 * Access Type IDs, {@link AccessTypeId}, that are associated with access types that do not require
 * an organization type, {@link OrganizationType}, to be specified.
 */
export const RootAccessTypeIds = [
  AccessTypeId.Admin,
  AccessTypeId.Evaluator,
  AccessTypeId.Instructor,
  AccessTypeId.Learner,
  AccessTypeId.Observer,
  AccessTypeId.Facilitator,
] as const;

export type RootAccessTypeId = (typeof RootAccessTypeIds)[number];

/**
 * Defines the role, {@link AppRole}, and organization type, {@link OrganizationType}
 * pairs that each access type, {@link AccessType} is associated with.
 *
 * Note:
 * ----
 * The reason that the source of truth for the {@link AccessTypes} relies on the Prisma forms
 * of the role, {@link AppRole}, and organization type, {@link OrganizationType}, is
 * because the Prisma forms of those "enums" are actually not enums - but rather, string literals.
 *
 * Since these values are string literals, it means that the members of the GraphQL forms of these
 * enums (which are actually enums) are *assignable* to their string literal equivalents.  However,
 * the Prisma string literal values are *not* assignable to the GraphQL enum values.
 *
 * This means that if the access types, {@link AccessTypes}, use the Prisma role,=
 * {@link AppRole}, and the Prisma organization type, {@link OrganizationType}, as
 * a source of truth, then it can be used for type inference and value lookup with either the
 * Prisma forms OR the GraphQL forms of these "enums".
 */
export const AccessTypes = {
  [AccessTypeId.Admin]: {
    id: AccessTypeId.Admin,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.ADMIN,
  },
  [AccessTypeId.EducationProviderAdmin]: {
    id: AccessTypeId.EducationProviderAdmin,
    organizationType: OrganizationType.EDUCATION_PROVIDER,
    role: AppRole.ADMIN,
  },
  [AccessTypeId.EmployerAdmin]: {
    id: AccessTypeId.EmployerAdmin,
    organizationType: OrganizationType.EMPLOYER,
    role: AppRole.ADMIN,
  },
  [AccessTypeId.Evaluator]: {
    id: AccessTypeId.Evaluator,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.EVALUATOR,
  },
  [AccessTypeId.Facilitator]: {
    id: AccessTypeId.Facilitator,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.FACILITATOR,
  },
  [AccessTypeId.Instructor]: {
    id: AccessTypeId.Instructor,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.INSTRUCTOR,
  },
  [AccessTypeId.Learner]: {
    id: AccessTypeId.Learner,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.LEARNER,
  },
  [AccessTypeId.Observer]: {
    id: AccessTypeId.Observer,
    organizationType: ANY_ORGANIZATION_TYPE,
    role: AppRole.OBSERVER,
  },
  [AccessTypeId.SponsorAdmin]: {
    id: AccessTypeId.SponsorAdmin,
    organizationType: OrganizationType.SPONSOR,
    role: AppRole.ADMIN,
  },
  [AccessTypeId.SystemAdmin]: {
    id: AccessTypeId.SystemAdmin,
    organizationType: OrganizationType.SYSTEM_ADMIN,
    role: AppRole.ADMIN,
  },
} as const satisfies {
  [key in AccessTypeId]: {
    readonly id: key;
    readonly organizationType: AnyOrganizationType | OrganizationType;
    readonly role: AppRole;
  };
};

export type AccessTypes = typeof AccessTypes;

/**
 * Represents an "access type", which is a representation of the type of "access" a given user has
 * based on their role, {@link AppRole}, and in some cases, their organization type,
 * {@link OrganizationType}.
 *
 * Each access type, {@link AccessType}, has the following properties:
 *
 * @param {AccessTypeId} id
 *   The unique identifier that represents the access type, {@link AccessTypeId}.
 * @param {AnyOrganizationType | OrganizationType} organizationType
 *   The organization type, {@link OrganizationType}, that the access type is associated with
 *
 *   If the organization type is {@link AnyOrganizationType}, then the access type does not depend
 *   on the organization type, {@link OrganizationType}, but only the role, {@link AppRole}.
 * @param {AppRole} role
 *   The role, {@link AppRole}, that the access type is associated with.
 *
 *   A user can have multiple access types, {@link AccessType}, based on the role,
 *   {@link AppRole} - organization type,
 *   {@link OrganizationType} pair that they are associated with.
 *
 *   For example, if the user has the role {@link AppRole.ADMIN}, and the organization type is
 *   {@link OrganizationType.EDUCATION_PROVIDER}, then the user has both the access type
 *   {@link AccessTypeId.Admin} and the access type {@link AccessTypeId.EducationProviderAdmin}.
 */
export type AccessType<A extends AccessTypeId = AccessTypeId> = A extends AccessTypeId
  ? Extract<ExtractValues<typeof AccessTypes>, { id: A }>
  : never;

type AccessTypeTraditionalParams = {
  readonly organizationType?: OrganizationType;
  readonly role: AppRole;
};

type AccessTypeOrganizationRole = {
  readonly organization: { readonly type: OrganizationType };
} & Pick<OrganizationRole, 'role'>;

type AccessTypeParams = AccessTypeOrganizationRole | AccessTypeTraditionalParams;

const isAccessTypeOrganizationRole = (
  params: AccessTypeOrganizationRole | AccessTypeTraditionalParams,
): params is AccessTypeOrganizationRole =>
  'organization' in params &&
  'role' in params &&
  isAppRole(params.role) &&
  'type' in params.organization &&
  isOrgType(params.organization.type);

const parseOrgType = (params: AccessTypeParams): OrganizationType | undefined =>
  isAccessTypeOrganizationRole(params) ? params.organization.type : params.organizationType;

/**
 * A union type that represents the access types, {@link AccessType}, that correspond to the
 * provided role, {@link AppRole}, and organization type, {@link OrganizationType}, defined by
 * the generic type parameter {@link P}.
 *
 * @template {P}
 *   The object parameters that contain the role, {@link AppRole}, and organization type,
 *   {@link OrganizationType}, for which to determine the access types.
 */
export type InferredAccessType<P extends AccessTypeParams> = P extends {
  organizationType: infer O extends OrganizationType;
  role: infer R extends AppRole;
}
  ? Extract<AccessType, { organizationType: `${O}` | AnyOrganizationType; role: `${R}` }>
  : P extends {
        organization: { type: infer O extends OrganizationType };
        role: infer R extends AppRole;
      }
    ? Extract<AccessType, { organizationType: `${O}` | AnyOrganizationType; role: `${R}` }>
    : P extends { role: infer R extends AppRole }
      ? Extract<AccessType, { organizationType: AnyOrganizationType; role: `${R}` }>
      : never;

/**
 * Determines (and returns) the access types, {@link AccessType}, that correspond to the provided
 * role, {@link AppRole}, and organization type, {@link OrganizationType}.
 *
 * @param {P} params:
 *   Parameters that include the role, {@link AppRole}, and optionally the organization type,
 *   {@link OrganizationType}, for which to determine the access types.
 *
 * @returns {InferredAccessType<R, O>[]}
 *   An array of access types, {@link AccessType}, that correspond to the provided role,
 *   {@link AppRole}, and organization type, {@link OrganizationType}.
 */
export const inferAccessTypes = <P extends AccessTypeParams>(
  params: P,
): InferredAccessType<P>[] => {
  const orgType = parseOrgType(params);

  /* Here, we ensure that the roles and organization between the Prisma & GraphQL forms of the
     enums are equivalent.  This will only perform the validation 1 time, regardless of how
     many times this function is called. */
  return Object.values(AccessTypes).filter(accessType => {
    /* If the access type has a organization type of 'AnyOrganizationType', then the access type
       determination is made solely based on the role. */
    const orgTypesConsistent =
      accessType.organizationType === ANY_ORGANIZATION_TYPE ||
      accessType.organizationType.valueOf() === orgType?.valueOf();

    return accessType.role.valueOf() === params.role.valueOf() && orgTypesConsistent;
  }) as InferredAccessType<P>[];
};

export type HasAccessType<
  P extends AccessTypeParams,
  T extends AccessTypeId,
> = T extends InferredAccessType<P>['id'] ? true : false;

export function hasAccessType<P extends AccessTypeParams, T extends AccessTypeId>(
  accessType: AccessType<T> | T,
  params: P,
): HasAccessType<P, T>;

export function hasAccessType<P extends AccessTypeParams>(
  accessType: (AccessType | AccessTypeId)[] | AccessType | AccessTypeId,
  params: null | P | undefined,
): params is P;

/**
 * Determines whether or not the provided access type, {@link AccessType | AccessTypeId}, or any of
 * the provided access types, {@link (AccessType | AccessTypeId)[]}, are consistent with the
 * provided role/organization type information, {@link AccessTypeParams}.
 *
 * @param {(AccessType<T> | AccessTypeId)[] | AccessType<T> | T} accessType
 *   The access type, {@link AccessType | AccessTypeId}, provided as either a single value or an
 *   array of values, for which the determination is being made as to whether or not the provided
 *   role information, {@link AccessTypeParams}, is consistent with.
 *   If provided as an array of values, the method will return 'true' if any of the access types
 *   in the array are consistent with the provided role information, {@link AccessTypeParams}.
 * @param {AccessTypeParams | null | undefined} params
 *   Parameters that include the role, {@link AppRole}, and the organization type,
 *   {@link OrganizationType}, which can be specified in multiple forms depending on usage.
 *
 * @example
 * // Returns 'true'
 * hasAccessType(AccessTypeId.SYSTEM_ADMIN, {
 *   role: AppRole.ADMIN,
 *   organizationType: OrganizationType.SYSTEM_ADMIN
 * });
 * @example
 * // Returns 'false'
 * hasAccessType(AccessTypeId.SYSTEM_ADMIN, {
 *   role: AppRole.ADMIN,
 *   organizationType: OrganizationType.SYSTEM_ADMIN
 * });
 *
 * @template P
 *   The role/organization type information, {@link AccessTypeParams}.
 * @template T
 *   The access type, {@link AccessType | AccessTypeId}.
 */
export function hasAccessType<P extends AccessTypeParams, T extends AccessTypeId>(
  accessType: (AccessType<T> | T)[] | AccessType<T> | T,
  params: null | P | undefined,
) {
  if (!params) {
    return false;
  }
  if (Array.isArray(accessType)) {
    return accessType.some(a => hasAccessType(a, params));
  }
  const accessTypes = inferAccessTypes(params);
  const accessTypeId = typeof accessType === 'string' ? accessType : accessType.id;
  return accessTypes.map(a => a.id).includes(accessTypeId) as HasAccessType<P, T>;
}
